import * as React from 'react';

import * as api from '@gitbook/api-client';
import {
    CUSTOMIZATION_ANNOUNCEMENT_MESSAGE_MAX_LENGTH,
    CUSTOMIZATION_ANNOUNCEMENT_MESSAGE_MIN_LENGTH,
} from '@gitbook/api-client';
import {
    ActionableContainer,
    Description,
    Group,
    Heading,
    Panel,
    PanelBody,
    Switch,
} from '@gitbook/spine-react';

import { useSiteContext, useUpgradeSite } from '../../../../state';
import { PaidFeatureIndicator } from '../../../Site/PaidFeatureIndicator';
import type { SitesCustomizationPanelProps } from '../../types';
import { AnnouncementLinkSettings } from './AnnouncementLinkSettings';
import { AnnouncementLinkTitleSettings } from './AnnouncementLinkTitleSettings';
import { AnnouncementMessageSettings } from './AnnouncementMessageSettings';
import { AnnouncementStyleSettings } from './AnnouncementStyleSettings';

// @site-announcement - Keep settings synced with backend by grepping that tag.

export function PanelAnnouncement(props: SitesCustomizationPanelProps): React.ReactElement {
    const { settings, setSettings } = props;
    const hasAnnouncement = settings.announcement !== undefined;
    const announcementVisible =
        settings.announcement?.enabled &&
        settings.announcement?.message.length >= CUSTOMIZATION_ANNOUNCEMENT_MESSAGE_MIN_LENGTH &&
        settings.announcement?.message.length <= CUSTOMIZATION_ANNOUNCEMENT_MESSAGE_MAX_LENGTH;
    const [wantsAnnouncement, setWantsAnnouncement] = React.useState(false);
    const opened = announcementVisible || wantsAnnouncement;

    const { site } = useSiteContext();
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesAdvancedCustomization);

    return (
        <Panel aria-label="Page">
            <ActionableContainer orientation="horizontal">
                <Group>
                    <Heading>Announcement</Heading>
                    <PaidFeatureIndicator upgrade={upgrade} isFeatureActive={announcementVisible} />
                </Group>
                <Switch
                    value={opened}
                    onValueChange={(checked) => {
                        if (hasAnnouncement) {
                            setSettings((prev) => ({
                                ...prev,
                                announcement: {
                                    message: '',
                                    style: 'info',
                                    ...prev.announcement,
                                    enabled: checked,
                                },
                            }));
                        }
                        setWantsAnnouncement(checked);
                    }}
                />
            </ActionableContainer>
            {opened ? (
                <PanelBody>
                    <Description>
                        Highlight important information in a banner at the top of your site.
                    </Description>
                    <AnnouncementMessageSettings {...props} />
                    <AnnouncementLinkSettings {...props} />
                    <AnnouncementLinkTitleSettings {...props} />
                    <AnnouncementStyleSettings {...props} />
                </PanelBody>
            ) : null}
        </Panel>
    );
}
